import { useAuth } from "@/app/providers/AuthProvider";
import { useTheme } from "@/app/providers/ThemeProvider";
import {
  Bell,
  Building2,
  LogOut,
  Monitor,
  Moon,
  Search,
  Settings,
  Sun,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { usePermission } from "@/hooks/usePermission";
import { cn } from "@/lib/cn";
import { toast } from "sonner";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const themeOptions = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
] as const;

export function MobileHeader() {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isAdmin } = usePermission();
  const navigate = useNavigate();

  const firstName = user?.name?.split(" ")[0] ?? "there";
  const roleLabel = user?.role?.replace(/_/g, " ") ?? "User";

  const handleSignOut = async () => {
    try {
      toast.info("Signing out...", {
        description: "Please wait while we end your session",
      });

      await signOut();

      toast.success("Signed out successfully", {
        description: "You have been logged out of your account.",
        icon: "👋",
      });

      navigate("/login");
    } catch (error) {
      toast.error("Failed to sign out", {
        description: error instanceof Error ? error.message : "An error occurred during logout",
      });
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--border)] bg-[var(--card)]/80 backdrop-blur-xl md:hidden safe-area-top">
      <div className="flex h-14 items-center justify-between px-4">
        {/* Left: Greeting */}
        <div className="flex items-center gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open account menu"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)] text-sm font-semibold text-white shadow-sm transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)]"
              >
                {firstName.charAt(0).toUpperCase()}
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[88dvh] rounded-t-[var(--radius-xl)] border-[var(--border)] px-0 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            >
              <SheetHeader className="border-b border-[var(--border)] px-5 pb-4 pt-5 text-left">
                <div className="flex items-center gap-3 pr-8">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-base font-semibold text-white">
                    {firstName.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <SheetTitle className="truncate text-base text-[var(--foreground)]">
                      {user?.name ?? firstName}
                    </SheetTitle>
                    <SheetDescription className="truncate capitalize text-[var(--color-mute)]">
                      {roleLabel}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="space-y-5 px-5">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-mute)]">
                    Theme
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {themeOptions.map((option) => {
                      const Icon = option.icon;
                      const isActive = theme === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setTheme(option.value)}
                          aria-pressed={isActive}
                          className={cn(
                            "flex min-h-16 flex-col items-center justify-center gap-1 rounded-[var(--radius-md)] border px-2 text-xs font-medium transition-colors",
                            isActive
                              ? "border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary-active)]"
                              : "border-[var(--border)] bg-[var(--card)] text-[var(--color-body)] hover:bg-[var(--muted)]",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <nav className="space-y-1" aria-label="Account shortcuts">
                  <SheetClose asChild>
                    <Link
                      to="/notifications"
                      className="flex items-center gap-3 rounded-[var(--radius-md)] px-1 py-3 text-sm font-medium text-[var(--color-body)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                    >
                      <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[var(--muted)]">
                        <Bell className="h-4 w-4" />
                        <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[var(--color-primary)]" />
                      </span>
                      <span>Notifications</span>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 rounded-[var(--radius-md)] px-1 py-3 text-sm font-medium text-[var(--color-body)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--muted)]">
                        <Settings className="h-4 w-4" />
                      </span>
                      <span>Settings</span>
                    </Link>
                  </SheetClose>
                </nav>
              </div>

              <SheetFooter className="border-t border-[var(--border)] px-5 pt-4">
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--foreground)] px-4 py-3 text-sm font-semibold text-[var(--background)] transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)]"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign out</span>
                  </button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Link
              to="/admin"
              aria-label="Open organisation admin"
              className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-body)] transition-colors hover:bg-[var(--muted)] active:bg-[var(--color-canvas-soft-2)]"
            >
              <Building2 className="h-5 w-5" />
            </Link>
          )}
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-body)] transition-colors hover:bg-[var(--muted)] active:bg-[var(--color-canvas-soft-2)]">
            <Search className="h-5 w-5" />
          </button>
          <NotificationBell className="flex h-9 w-9 items-center justify-center rounded-full p-0" aria-label="Open notifications" />
        </div>
      </div>
    </header>
  );
}
